import type { ReactNode } from 'react';
import clsx from 'clsx';
import type { CompletionRollup } from './completion-rollup';
import { IncludeExcludeControl } from './IncludeExcludeControl';

export type SubsectionStatus = 'included' | 'excluded';

export type SubsectionRowProps = {
  title: string;
  testCaseCount: number;
  /**
   * F-013 (FR-010): the node is an instructional template node — render the
   * Instructional-Section Decision control instead of the bulk-triage chips.
   * Default-included: an undecided node renders with no fill.
   */
  instructional?: boolean;
  /** The current decision state (undecided ⇒ included by default). */
  decisionState?: 'undecided' | 'included' | 'excluded';
  onDecideInclude?: () => void;
  onDecideExclude?: () => void;
  onClearDecision?: () => void;
  /**
   * Replaces the auto-generated "N test cases" subtitle (e.g. with a
   * "+ Add Test Cases" prompt link for newly-created subsections).
   */
  subtitle?: ReactNode;
  /**
   * Inline node rendered to the right of the title (e.g. a small "N"
   * badge for newly-created subsections).
   */
  titleSuffix?: ReactNode;
  /**
   * Feature 025 — the Subsection's Completion Rollup. When present, a static
   * "Marked as Complete X/Y" indicator is shown in the header (collapsed AND
   * expanded, FR-012), as a sibling of the toggle button. The orchestrator omits
   * it for a node with zero available cases (US3), so an empty/instructional/
   * all-excluded Subsection shows its inclusion state instead. Separate from the
   * per-row completion control (FR-011) — this is the count only.
   */
  completion?: CompletionRollup;
  /**
   * Feature 146 — the count of undecided byte-for-byte template matches in this
   * Subsection (the qualifying set the bulk-approve would include). The control
   * renders only when this is > 0 AND `onApproveExactMatches` is supplied, so it
   * self-hides when there is nothing to approve (FR-007). The count rides the
   * control's accessible name (FR-008/SC-008); it is never decided here — the
   * orchestrator (apps/web) computes it and the gating.
   */
  exactMatchCount?: number;
  /**
   * Feature 146 — invoked when the editor clicks "Approve exact matches". Supplied
   * only for an actor with edit permission (FR-006), so a viewer never receives a
   * handler and the control is absent. Presentational only: this component owns no
   * decision logic — it just calls back.
   */
  onApproveExactMatches?: () => void;
  /**
   * When set, the action +/− pair is replaced by a status pill
   * (uat-27 included-rows variant).
   */
  status?: SubsectionStatus;
  /**
   * When true the chevron rotates to indicate "expanded". Caller
   * renders the actual expanded content via `children`.
   */
  expanded?: boolean;
  onToggle?: (next: boolean) => void;
  /**
   * F-145 (FR-001/002): exclude the whole subsection. The ONLY bulk action on an
   * "In the script" subsection — inclusion is the implicit default, so there is
   * no bulk include affordance. The caller guards approved cases (US2) and runs
   * the all-or-nothing cascade (US4).
   */
  onExclude?: () => void;
  /**
   * F-145 (FR-004): Restore a fully "Excluded" subsection — clears every child
   * back to undecided (the existing reverse-to-needs-review behaviour). Offered
   * ONLY on the "Excluded" presentation (`status="excluded"`); a part-excluded
   * subsection presents as "In the script" and has no one-click restore.
   */
  onReverse?: () => void;
  showCompleted?: boolean;
  /** Preserve the action column while hiding no-op actions when a subsection has no child cases. */
  canTriage?: boolean;
  /** Preserve title/count geometry while hiding disclosure affordances for empty subsections. */
  canExpand?: boolean;
  /**
   * Optional drag handle (feature 015), rendered inside the row's relative
   * wrapper as a SIBLING of the toggle button. Now a visually-hidden handle
   * (keyboard reorder + a11y/test locator) — the pointer drag target is the
   * whole row via `dragListeners`.
   */
  dragHandle?: ReactNode;
  /**
   * Sortable pointer listeners spread on the visible row so the whole header is
   * the drag target (no grip icon). The activation distance separates a
   * click-to-expand from a drag. Omitted on the expanded body so nested
   * test-case drags aren't shadowed.
   */
  dragListeners?: Record<string, unknown>;
  /** Optional content rendered beneath the row when `expanded` is true. */
  children?: ReactNode;
};

const wrapBase = clsx(
  'relative border-b border-border last:border-b-0',
  'data-[expanded=true]:bg-ink-50',
  // Decided subsections carry a 3px accent down the left edge — green when
  // included (Figma 1061-113867), red when excluded (Figma 584-16085).
  'data-[status=included]:border-l-[3px] data-[status=included]:border-l-[#1d7c5f]',
  'data-[status=excluded]:border-l-[3px] data-[status=excluded]:border-l-[#d94433]',
);

// Figma uat-02: subsection row is 55px tall. Chevron sits at left edge with 16px x-pad,
// title and "N test cases" subtitle stack with 2px gap; actions hug the right edge.
const rowBase = 'grid min-h-[55px] items-center px-0 py-0 hover:bg-ink-50';

const noop = () => {};

export function SubsectionRow({
  title,
  testCaseCount,
  instructional,
  decisionState,
  onDecideInclude,
  onDecideExclude,
  onClearDecision,
  subtitle,
  titleSuffix,
  completion,
  exactMatchCount,
  onApproveExactMatches,
  status,
  expanded = false,
  onToggle,
  onExclude,
  onReverse,
  showCompleted = true,
  canTriage = true,
  canExpand = true,
  dragHandle,
  dragListeners,
  children,
}: SubsectionRowProps) {
  const isExpanded = canExpand && expanded;
  // F-038 US3 (FR-009, I-CG-08): the subsection completion is DERIVED from the
  // F-025 rollup — complete iff every available case is complete. Omitted when
  // there are no available cases (the orchestrator drops the `completion` prop).
  const isSubsectionComplete =
    completion !== undefined &&
    completion.available > 0 &&
    completion.complete === completion.available;
  // F-146 (FR-006/007): render the bulk-approve control ONLY when an editor
  // handler is supplied AND there is at least one qualifying case to approve. Both
  // conditions are decided upstream (apps/web) — a viewer gets no handler, an
  // empty/decided subsection gets count 0 — so the control self-hides here.
  const showApproveExactMatches = onApproveExactMatches !== undefined && (exactMatchCount ?? 0) > 0;
  const titleContent = (
    <>
      {canExpand ? (
        // Fixed 16px box: the glyph rotates inside it so the `auto` grid column
        // never resizes, keeping the title column from shifting on toggle.
        <span className="inline-flex h-4 w-4 flex-shrink-0 items-center justify-center text-[#000212]">
          {/* Figma thin chevron (stroke #000212), rotates to ⌄ when expanded. */}
          <svg
            data-icon="chevronRight"
            width="6"
            height="9"
            viewBox="0 0 6 9"
            fill="none"
            aria-hidden="true"
            className={clsx('transition-transform', isExpanded && 'rotate-90')}
          >
            <path
              d="M0.494141 0.564453L4.49414 4.06445L0.494141 7.56445"
              stroke="currentColor"
              strokeWidth="1.5"
            />
          </svg>
        </span>
      ) : (
        <span data-subsection-chevron-spacer aria-hidden="true" className="h-4 w-4" />
      )}
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2 text-xl font-semibold leading-[20px] text-text-primary">
          {/* Decided rows dim the TITLE to read as "locked in" (Figma 1061-113867)
              — but only the title text, never the title-suffix badge (e.g. the
              novel "N"), which keeps its own 100%-opacity colour. */}
          <span
            className={clsx(
              'block min-w-0 truncate',
              (status === 'included' || status === 'excluded') && 'opacity-60',
            )}
          >
            {title}
          </span>
          {titleSuffix}
        </div>
        <div className="mt-[2px] truncate text-sm leading-[17px] text-text-secondary">
          {subtitle ?? `${testCaseCount} test ${testCaseCount === 1 ? 'case' : 'cases'}`}
        </div>
      </div>
    </>
  );

  return (
    <div
      data-expanded={isExpanded ? 'true' : 'false'}
      data-status={status ?? 'none'}
      className={wrapBase}
    >
      {dragHandle}
      <div
        data-subsection-row
        {...(dragListeners ?? {})}
        className={clsx(
          rowBase,
          'relative',
          dragListeners && 'touch-none',
          isExpanded ? 'bg-ink-50' : 'bg-surface-card',
          showCompleted
            ? 'grid-cols-[minmax(0,1fr)_127px_130px]'
            : 'grid-cols-[minmax(0,1fr)_127px]',
        )}
      >
        {status === 'excluded' && (
          // Excluded row: a faint diagonal-hatch overlay strikes the row out
          // (Figma 584-16085). Non-interactive so the field/seal stay clickable.
          <div
            aria-hidden="true"
            data-subsection-excluded-overlay
            className="pointer-events-none absolute inset-0 z-[1]"
            style={{
              background:
                'repeating-linear-gradient(120deg, rgba(217,217,217,0.3) 0, rgba(217,217,217,0.3) 3px, transparent 3px, transparent 11px)',
            }}
          />
        )}
        <div className="flex h-full min-w-0 items-center">
          {canExpand ? (
            <button
              type="button"
              aria-expanded={isExpanded}
              onClick={() => onToggle?.(!isExpanded)}
              className="grid h-full min-w-0 flex-1 cursor-pointer grid-cols-[auto_1fr] items-center gap-3 border-0 bg-transparent px-4 py-0 text-left"
            >
              {titleContent}
            </button>
          ) : (
            <div className="grid h-full min-w-0 flex-1 grid-cols-[auto_1fr] items-center gap-3 px-4 py-0 text-left">
              {titleContent}
            </div>
          )}
          {showApproveExactMatches ? (
            // F-146 (US3): compact "Approve exact matches" affordance, placed as a
            // flex-shrink-0 sibling to the LEFT of the completion indicator so both
            // can render at once (some cases complete AND some undecided exact
            // matches) and the completion text keeps its right-edge position. The
            // count rides the accessible name (no room for a visible count, D4).
            <button
              type="button"
              data-subsection-approve-exact
              aria-label={`Approve ${exactMatchCount} exact template matches in ${title}`}
              onClick={onApproveExactMatches}
              className="mr-4 inline-flex h-7 flex-shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-[#1d7c5f] px-2 text-xs font-medium text-[#1d7c5f] hover:bg-[#dfede9]"
            >
              {/* Double-check "approve all" glyph (decorative — name is on the button). */}
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
                className="flex-shrink-0"
              >
                <path
                  d="M1.5 8.5L4.5 11.5L10 5.5M7 11.5L8 12.5L14.5 5.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Approve matches
            </button>
          ) : null}
          {completion ? (
            // FR-012: a static "Marked as Complete X/Y" indicator, right-aligned
            // within the title column as a SIBLING of the toggle button (so the
            // button's accessible name and the fixed row geometry are untouched).
            <span
              data-subsection-completion
              className="flex-shrink-0 whitespace-nowrap pr-4 text-sm leading-[17px] tabular-nums text-text-secondary"
            >
              Marked as Complete {completion.complete}/{completion.available}
            </span>
          ) : null}
        </div>
        <div
          data-subsection-action-cell
          className={clsx(
            'relative z-[2] flex h-full items-center justify-center border-x border-border',
            // A decided subsection fills the action cell with a full-width field
            // (Figma 1061-113867 / 587-15733): green INCLUDED or red EXCLUDED.
            status === 'included' && 'bg-[#dfede9]',
            status === 'excluded' && 'bg-[#fbebe9]',
          )}
        >
          {instructional && (
            <IncludeExcludeControl
              size="compact"
              recommendation={null}
              selection={decisionState ?? 'undecided'}
              ariaSuffix={title}
              activeSurface={isExpanded}
              onInclude={onDecideInclude ?? noop}
              onExclude={onDecideExclude ?? noop}
              onClear={onClearDecision ?? noop}
            />
          )}
          {!instructional &&
            canTriage &&
            (status === 'excluded' ? (
              /* F-145 (FR-001/004): an "Excluded" subsection fills the action cell
                 with the full-width EXCLUDED field (Figma 587-15733). Clicking it
                 RESTORES the subsection — every child back to undecided (onReverse).
                 The state is conveyed by the "Excluded" text + aria-pressed, never
                 colour alone (FR-014/SC-008). */
              <button
                type="button"
                aria-label={`Excluded ${title}, click to restore`}
                aria-pressed
                onClick={() => (onReverse ?? noop)()}
                className="flex h-full w-full cursor-pointer items-center justify-center text-xs font-medium uppercase tracking-[-0.1504px] text-[#d94433]"
              >
                Excluded
              </button>
            ) : (
              /* F-145 (FR-001/002, I-SI-01): "In the script" is the default — the
                 ONLY bulk action is a single Exclude subsection. There is no bulk
                 include (inclusion is implicit) and no +/- pair; excluding
                 individual cases is per-case curation, never a third subsection
                 control state. The destructive cue is the red-on-hover affordance,
                 not a filled state. */
              <button
                type="button"
                aria-label={`Exclude subsection ${title}`}
                onClick={() => (onExclude ?? noop)()}
                className="flex h-full w-full cursor-pointer items-center justify-center whitespace-nowrap px-1 text-[11px] font-medium uppercase tracking-[-0.1504px] text-text-secondary hover:text-[#d94433]"
              >
                Exclude subsection
              </button>
            ))}
        </div>
        {showCompleted && (
          <div
            data-subsection-completed-cell
            className="relative z-[2] flex h-full items-center justify-center"
          >
            {/* F-038 US3 (FR-009/010, I-CG-07/08): a DERIVED, read-only completion
                indicator — never a control. The cascade is deleted; the rollup is
                the single source of truth. Rendered with the Figma seal (553-8686):
                filled green when every case is complete, hollow grey otherwise. */}
            {completion && (
              <span
                data-subsection-complete-indicator
                data-completed={isSubsectionComplete ? 'true' : 'false'}
                aria-label={
                  isSubsectionComplete
                    ? `${title}: all cases marked complete`
                    : `${title}: ${completion.complete} of ${completion.available} cases marked complete`
                }
                {...(isSubsectionComplete
                  ? {}
                  : {
                      title: `${completion.complete} of ${completion.available} cases marked complete — complete every case to finish this subsection`,
                    })}
                className={clsx(
                  'inline-flex h-5 w-5 flex-shrink-0 items-center justify-center',
                  isSubsectionComplete ? 'text-[#1d7c5f]' : 'text-[#6d7287]',
                )}
              >
                {/* Figma "seal" completion mark (node 553-8686). */}
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill={isSubsectionComplete ? 'currentColor' : 'none'}
                  aria-hidden="true"
                >
                  <path
                    d="M11.0752 2.69336L11.2842 2.875L11.5488 2.79395L14.4141 1.91992L15.0908 4.83887L15.1533 5.1084L15.415 5.19824L18.2471 6.17578L17.0781 8.93457L16.9707 9.18945L17.1289 9.41602L18.8457 11.8711L16.2783 13.417L16.042 13.5596L16.0361 13.8359L15.9814 16.832L12.9971 16.5723L12.7217 16.5488L12.5547 16.7695L10.75 19.1611L8.4873 17.1973L8.27832 17.0156L8.01367 17.0967L5.14746 17.9707L4.47168 15.0518L4.40918 14.7822L4.14844 14.6914L1.31543 13.7139L2.48438 10.9561L2.59277 10.7012L2.43359 10.4736L0.716797 8.01758L3.28418 6.47363L3.52148 6.33105L3.52637 6.05469L3.58008 3.05762L6.56543 3.31738L6.8418 3.3418L7.00781 3.12109L8.8125 0.728516L11.0752 2.69336Z"
                    stroke="currentColor"
                  />
                  <path
                    d="M6.7 10.1L9 12.3L13.3 7.7"
                    fill="none"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={isSubsectionComplete ? 'stroke-white' : 'stroke-transparent'}
                  />
                </svg>
              </span>
            )}
          </div>
        )}
      </div>
      {isExpanded && children && (
        <div data-expanded-subsection-body className="w-full">
          {children}
        </div>
      )}
    </div>
  );
}
